import React from 'react';
import HelperMethods from '../../../../services/exports/HelperMethods';
import usePermissions, { ManagerRole } from '../../../../hooks/auth/usePermissions';
import Metric from '../../../../components/stats/Metric';
import MetricsContainer from '../../../../components/stats/MetricsContainer';
import useHelpers from '../../../../hooks/context/useHelpers';
import StatisticsProvider from '../../StatisticsProvider';
import useStatistics from '../../../../hooks/data/useStatistics';
import useServiceProvider from '../../../../hooks/data/useServiceProvider';
import {
  PLG_HIDDEN_GENERAL_METRICS,
  PLG_HIDDEN_GENERAL_METRIC_FIELDS,
  excludePlgHiddenFields,
  isPlgHiddenMetric,
} from '../../../../services/tenant/plgOnboarding';

function Metrics() {
  const { stats, isMetricLoading, getMetricLabel } = useStatistics();
  const { formatCurrency } = useHelpers();
  // Billed revenue / cash income / processing fees / refunds are empty for
  // self-serve (PLG) partners — they settle outside the platform until go-live.
  const { hasPlgOnboarding } = useServiceProvider();

  const moneyFormatter = (value) => formatCurrency(value);
  const percentageFormatter = (value) => HelperMethods.formatPercentage(value);

  const metrics = stats
    ? [
        <Metric
          key={getMetricLabel('gmv')}
          name={getMetricLabel('gmv')}
          items={[
            { name: getMetricLabel('gmv'), ...stats.gmv },
            { name: getMetricLabel('gift_cards_gmv'), ...stats.gift_cards_gmv },
          ]}
          formatter={moneyFormatter}
          loading={isMetricLoading(['gmv', 'gift_cards_gmv'])}
          mainMetrics={['gmv', 'gift_cards_gmv']}
        />,
        !isPlgHiddenMetric('net_billed_revenue', PLG_HIDDEN_GENERAL_METRICS, hasPlgOnboarding) && (
          <Metric
            key={getMetricLabel('net_billed_revenue')}
            name={getMetricLabel('net_billed_revenue')}
            loading={isMetricLoading([
              'net_billed_revenue',
              'net_billed_revenue_subscription',
              'net_billed_revenue_service_fee',
              'net_billed_revenue_external_delivery_platform_fee',
              'net_billed_revenue_other',
              'net_billed_revenue_external_delivery_passthrough_fee',
              'net_billed_revenue_gift_cards',
              'net_billed_revenue_platform_processing_fee',
            ])}
            mainMetrics={[
              'net_billed_revenue',
              'net_billed_revenue_subscription',
              'net_billed_revenue_service_fee',
              'net_billed_revenue_external_delivery_platform_fee',
              'net_billed_revenue_other',
              'net_billed_revenue_external_delivery_passthrough_fee',
              'net_billed_revenue_gift_cards',
              'net_billed_revenue_platform_processing_fee',
            ]}
            items={[
              {
                name: getMetricLabel('net_billed_revenue'),
                ...stats.net_billed_revenue,
              },
              {
                name: getMetricLabel('net_billed_revenue_subscription'),
                ...stats.net_billed_revenue_subscription,
              },
              {
                name: getMetricLabel('net_billed_revenue_service_fee'),
                ...stats.net_billed_revenue_service_fee,
              },
              {
                name: getMetricLabel('net_billed_revenue_external_delivery_platform_fee'),
                ...stats.net_billed_revenue_external_delivery_platform_fee,
              },
              {
                name: getMetricLabel('net_billed_revenue_other'),
                ...stats.net_billed_revenue_other,
              },
              {
                name: getMetricLabel('net_billed_revenue_external_delivery_passthrough_fee'),
                ...stats.net_billed_revenue_external_delivery_passthrough_fee,
              },
              {
                name: getMetricLabel('net_billed_revenue_gift_cards'),
                ...stats.net_billed_revenue_gift_cards,
              },
              {
                name: getMetricLabel('net_billed_revenue_platform_processing_fee'),
                ...stats.net_billed_revenue_platform_processing_fee,
              },
            ]}
            formatter={moneyFormatter}
          />
        ),
        !isPlgHiddenMetric('gross_cash_income', PLG_HIDDEN_GENERAL_METRICS, hasPlgOnboarding) && (
          <Metric
            key={getMetricLabel('gross_cash_income')}
            name={getMetricLabel('gross_cash_income')}
            loading={isMetricLoading([
              'gross_cash_income',
              'gross_cash_income_subscription',
              'gross_cash_income_service_fee',
              'gross_cash_income_external_delivery_platform_fee',
              'gross_cash_income_other',
              'gross_cash_income_external_delivery_passthrough_fee',
              'gross_cash_income_gift_cards',
              'gross_cash_income_platform_processing_fee',
            ])}
            mainMetrics={[
              'gross_cash_income',
              'gross_cash_income_subscription',
              'gross_cash_income_service_fee',
              'gross_cash_income_external_delivery_platform_fee',
              'gross_cash_income_other',
              'gross_cash_income_external_delivery_passthrough_fee',
              'gross_cash_income_gift_cards',
              'gross_cash_income_platform_processing_fee',
            ]}
            items={[
              {
                name: getMetricLabel('gross_cash_income'),
                ...stats.gross_cash_income,
              },
              {
                name: getMetricLabel('gross_cash_income_subscription'),
                ...stats.gross_cash_income_subscription,
              },
              {
                name: getMetricLabel('gross_cash_income_service_fee'),
                ...stats.gross_cash_income_service_fee,
              },
              {
                name: getMetricLabel('gross_cash_income_external_delivery_platform_fee'),
                ...stats.gross_cash_income_external_delivery_platform_fee,
              },
              {
                name: getMetricLabel('gross_cash_income_other'),
                ...stats.gross_cash_income_other,
              },
              {
                name: getMetricLabel('gross_cash_income_external_delivery_passthrough_fee'),
                ...stats.gross_cash_income_external_delivery_passthrough_fee,
              },
              {
                name: getMetricLabel('gross_cash_income_gift_cards'),
                ...stats.gross_cash_income_gift_cards,
              },
              {
                name: getMetricLabel('gross_cash_income_platform_processing_fee'),
                ...stats.gross_cash_income_platform_processing_fee,
              },
            ]}
            formatter={moneyFormatter}
          />
        ),
        !isPlgHiddenMetric('net_cash_income', PLG_HIDDEN_GENERAL_METRICS, hasPlgOnboarding) && (
          <Metric
            key={getMetricLabel('net_cash_income')}
            name={getMetricLabel('net_cash_income')}
            loading={isMetricLoading([
              'net_cash_income',
              'net_cash_income_subscription',
              'net_cash_income_service_fee',
              'net_cash_income_external_delivery_platform_fee',
              'net_cash_income_other',
              'net_cash_income_external_delivery_passthrough_fee',
              'net_cash_income_gift_cards',
              'net_cash_income_platform_processing_fee',
            ])}
            mainMetrics={[
              'net_cash_income',
              'net_cash_income_subscription',
              'net_cash_income_service_fee',
              'net_cash_income_external_delivery_platform_fee',
              'net_cash_income_other',
              'net_cash_income_external_delivery_passthrough_fee',
              'net_cash_income_gift_cards',
              'net_cash_income_platform_processing_fee',
            ]}
            items={[
              {
                name: getMetricLabel('net_cash_income'),
                ...stats.net_cash_income,
              },
              {
                name: getMetricLabel('net_cash_income_subscription'),
                ...stats.net_cash_income_subscription,
              },
              {
                name: getMetricLabel('net_cash_income_service_fee'),
                ...stats.net_cash_income_service_fee,
              },
              {
                name: getMetricLabel('net_cash_income_external_delivery_platform_fee'),
                ...stats.net_cash_income_external_delivery_platform_fee,
              },
              {
                name: getMetricLabel('net_cash_income_other'),
                ...stats.net_cash_income_other,
              },
              {
                name: getMetricLabel('net_cash_income_external_delivery_passthrough_fee'),
                ...stats.net_cash_income_external_delivery_passthrough_fee,
              },
              {
                name: getMetricLabel('net_cash_income_gift_cards'),
                ...stats.net_cash_income_gift_cards,
              },
              {
                name: getMetricLabel('net_cash_income_platform_processing_fee'),
                ...stats.net_cash_income_platform_processing_fee,
              },
            ]}
            formatter={moneyFormatter}
          />
        ),
        !isPlgHiddenMetric('manual_connect_transfers', PLG_HIDDEN_GENERAL_METRICS, hasPlgOnboarding) && (
          <Metric
            key={getMetricLabel('manual_connect_transfers')}
            name={getMetricLabel('manual_connect_transfers')}
            loading={isMetricLoading(['manual_connect_transfers'])}
            mainMetrics={['manual_connect_transfers']}
            items={stats.manual_connect_transfers}
            formatter={moneyFormatter}
            flipChange
          />
        ),
        !isPlgHiddenMetric('processing_fees', PLG_HIDDEN_GENERAL_METRICS, hasPlgOnboarding) && (
          <Metric
            key={getMetricLabel('processing_fees')}
            name={getMetricLabel('processing_fees')}
            loading={isMetricLoading([
              'processing_fees',
              'processing_fees_stripe',
              'processing_fees_paypal',
              'platform_processing_fees',
            ])}
            mainMetrics={[
              'processing_fees',
              'processing_fees_stripe',
              'processing_fees_paypal',
              'platform_processing_fees',
            ]}
            items={[
              {
                name: getMetricLabel('processing_fees'),
                ...stats.processing_fees,
              },
              {
                name: getMetricLabel('processing_fees_stripe'),
                ...stats.processing_fees_stripe,
              },
              {
                name: getMetricLabel('processing_fees_paypal'),
                ...stats.processing_fees_paypal,
              },
              {
                name: getMetricLabel('platform_processing_fees'),
                ...stats.platform_processing_fees,
              },
            ]}
            formatter={moneyFormatter}
          />
        ),
        <Metric
          key={getMetricLabel('rejected_orders')}
          name={getMetricLabel('rejected_orders')}
          loading={isMetricLoading([
            'rejected_orders_amount',
            'rejected_orders_count',
            'rejected_orders_rate',
            'auto_rejected_orders_rate',
            'manually_rejected_orders_rate',
          ])}
          mainMetrics={[
            'rejected_orders_amount',
            'rejected_orders_count',
            'rejected_orders_rate',
            'rejected_orders_rate_breakdown',
          ]}
          excludedHistoryMetrics={['rejected_orders_rate_breakdown', 'rejected_orders_rate']}
          items={[
            {
              name: getMetricLabel('rejected_orders_amount'),
              formatter: moneyFormatter,
              ...stats.rejected_orders_amount,
            },
            {
              name: getMetricLabel('rejected_orders_count'),
              ...stats.rejected_orders_count,
            },
            {
              name: getMetricLabel('rejected_orders_rate'),
              formatter: percentageFormatter,
              ...stats.rejected_orders_rate,
            },
            {
              name: getMetricLabel('rejected_orders_rate_breakdown'),
              formatter: percentageFormatter,
              value: [stats.auto_rejected_orders_rate?.value, stats.manually_rejected_orders_rate?.value],
              previous: [stats.auto_rejected_orders_rate?.previous, stats.manually_rejected_orders_rate?.previous],
              change: [stats.auto_rejected_orders_rate?.change, stats.manually_rejected_orders_rate?.change],
            },
          ]}
        />,
        <Metric
          key={getMetricLabel('average_order_value')}
          name={getMetricLabel('average_order_value')}
          items={stats.average_order_value}
          formatter={moneyFormatter}
          loading={isMetricLoading(['average_order_value'])}
          mainMetrics={['average_order_value']}
        />,
        <Metric
          key={getMetricLabel('tips')}
          name={getMetricLabel('tips')}
          items={stats.tips}
          formatter={moneyFormatter}
          loading={isMetricLoading(['tips'])}
          mainMetrics={['tips']}
        />,
        <Metric
          key={getMetricLabel('orders')}
          name={getMetricLabel('orders')}
          loading={isMetricLoading(['orders_count', 'pickup_orders_rate', 'delivery_orders_rate', 'other_orders_rate'])}
          mainMetrics={['orders_count', 'pickup_orders_rate', 'delivery_orders_rate', 'other_orders_rate']}
          excludedHistoryMetrics={['*']}
          items={[
            {
              name: getMetricLabel('total_orders'),
              ...stats.orders_count,
            },
            {
              name: getMetricLabel('pickup_orders_rate'),
              formatter: percentageFormatter,
              ...stats.pickup_orders_rate,
            },
            {
              name: getMetricLabel('delivery_orders_rate'),
              formatter: percentageFormatter,
              ...stats.delivery_orders_rate,
            },
            {
              name: getMetricLabel('other_orders_rate'),
              formatter: percentageFormatter,
              ...stats.other_orders_rate,
            },
          ]}
        />,
        <Metric
          key={getMetricLabel('customers')}
          name={getMetricLabel('customers')}
          loading={isMetricLoading([
            'customers',
            'guest_customers',
            'signed_up_customers',
            'new_customers',
            'returning_customers',
          ])}
          mainMetrics={['customers', 'customers_auth_state', 'new_customers', 'returning_customers']}
          excludedHistoryMetrics={['*']}
          items={[
            {
              name: getMetricLabel('customers'),
              ...stats.customers,
            },
            {
              name: getMetricLabel('customers_auth_state'),
              value: [stats.guest_customers?.value, stats.signed_up_customers?.value],
              previous: [stats.guest_customers?.previous, stats.signed_up_customers?.previous],
              change: [stats.guest_customers?.change, stats.signed_up_customers?.change],
            },
            {
              name: getMetricLabel('new_customers'),
              ...stats.new_customers,
            },
            {
              name: getMetricLabel('returning_customers'),
              ...stats.returning_customers,
            },
          ]}
        />,
        <Metric
          key={getMetricLabel('reservations')}
          name={getMetricLabel('reservations')}
          loading={isMetricLoading(['total_reservations', 'website_reservations', 'google_reservations'])}
          reservationMetrics={['total_reservations', 'website_reservations', 'google_reservations']}
          items={[
            {
              name: getMetricLabel('total_reservations'),
              ...stats.total_reservations,
            },
            {
              name: getMetricLabel('website_reservations'),
              ...stats.website_reservations,
            },
            {
              name: getMetricLabel('google_reservations'),
              ...stats.google_reservations,
            },
          ]}
        />,
      ].filter(Boolean)
    : [];

  return <MetricsContainer metrics={metrics} />;
}

export default function General({ dateRangeFilter, renderDateRangeFilter }) {
  const { hasAnyRole } = usePermissions();
  const { hasPlgOnboarding } = useServiceProvider();

  return (
    hasAnyRole([ManagerRole.SuperAdmin, ManagerRole.Admin]) && (
      <StatisticsProvider
        dateRangeFilter={dateRangeFilter}
        mainMetrics={excludePlgHiddenFields(
          [
            'gmv',
            'gift_cards_gmv',
            'net_billed_revenue',
            'net_billed_revenue_subscription',
            'net_billed_revenue_service_fee',
            'net_billed_revenue_external_delivery_platform_fee',
            'net_billed_revenue_other',
            'net_billed_revenue_external_delivery_passthrough_fee',
            'net_billed_revenue_gift_cards',
            'net_billed_revenue_platform_processing_fee',
            'gross_cash_income',
            'gross_cash_income_subscription',
            'gross_cash_income_service_fee',
            'gross_cash_income_external_delivery_platform_fee',
            'gross_cash_income_other',
            'gross_cash_income_external_delivery_passthrough_fee',
            'gross_cash_income_gift_cards',
            'gross_cash_income_platform_processing_fee',
            'net_cash_income',
            'net_cash_income_subscription',
            'net_cash_income_service_fee',
            'net_cash_income_external_delivery_platform_fee',
            'net_cash_income_other',
            'net_cash_income_external_delivery_passthrough_fee',
            'net_cash_income_gift_cards',
            'net_cash_income_platform_processing_fee',
            'manual_connect_transfers',
            'processing_fees',
            'processing_fees_stripe',
            'processing_fees_paypal',
            'platform_processing_fees',
            'rejected_orders_amount',
            'rejected_orders_count',
            'rejected_orders_rate',
            'auto_rejected_orders_rate',
            'manually_rejected_orders_rate',
            'average_order_value',
            'tips',
            'orders_count',
            'pickup_orders_rate',
            'delivery_orders_rate',
            'other_orders_rate',
            'customers',
            'customers_auth_state',
            'new_customers',
            'returning_customers',
          ],
          PLG_HIDDEN_GENERAL_METRIC_FIELDS,
          hasPlgOnboarding,
        )}
        reservationMetrics={['total_reservations', 'website_reservations', 'google_reservations']}
      >
        {renderDateRangeFilter()}
        <Metrics />
      </StatisticsProvider>
    )
  );
}
